/**
 * S3 document attachment markers — embedded in chat message text for persistence.
 *
 * Format: `[S3_DOC_META:<base64url(JSON)>]`
 *
 * Why base64url: document names can contain any character, including `}` and
 * `]`. Base64url payloads contain only `[A-Za-z0-9_=-]`, so the closing `]` is
 * unambiguous to a regex.
 */

export interface S3DocMeta {
	documentId: number;
	fileType: string;
	name: string;
}

const PREFIX = '[S3_DOC_META:';
const SUFFIX = ']';
const MARKER_REGEX_SOURCE = '\\[S3_DOC_META:([^\\]]+)\\]';

function toBase64Url(input: string): string {
	if (typeof Buffer !== 'undefined') {
		return Buffer.from(input, 'utf-8').toString('base64').replace(/\+/g, '-').replace(/\//g, '_');
	}
	const bytes = new TextEncoder().encode(input);
	let bin = '';
	for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
	return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_');
}

function fromBase64Url(input: string): string {
	const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
	if (typeof Buffer !== 'undefined') {
		return Buffer.from(b64, 'base64').toString('utf-8');
	}
	const bin = atob(b64);
	const bytes = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
	return new TextDecoder().decode(bytes);
}

export function buildS3DocMetaMarker(meta: S3DocMeta): string {
	return `${PREFIX}${toBase64Url(JSON.stringify(meta))}${SUFFIX}`;
}

function parsePayload(payload: string): S3DocMeta | null {
	try {
		const parsed = JSON.parse(fromBase64Url(payload)) as S3DocMeta;
		if (typeof parsed?.documentId !== 'number') return null;
		return parsed;
	} catch {
		return null;
	}
}

export function parseS3DocMetaMarkers(text: string): S3DocMeta[] {
	const results: S3DocMeta[] = [];
	const regex = new RegExp(MARKER_REGEX_SOURCE, 'g');
	let match: RegExpExecArray | null;
	while ((match = regex.exec(text)) !== null) {
		const meta = parsePayload(match[1]!);
		if (meta) results.push(meta);
	}
	return results;
}

export function extractS3DocMetaIds(text: string): number[] {
	return parseS3DocMetaMarkers(text).map((m) => m.documentId);
}

export function stripS3DocMetaMarkers(text: string): string {
	return text.replace(new RegExp(MARKER_REGEX_SOURCE + '\\s*', 'g'), '').trim();
}
